// @ts-nocheck
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { ArrowRight, CaretRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Text, XStack, YStack } from 'tamagui';

import { Header, Footer, SubscribeForm } from '../../components/landing';
import { getUseCaseBySlug, USE_CASES } from './useCaseData';

export default function UseCasePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  const useCase = getUseCaseBySlug(slug || '');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!useCase) {
    return (
      <YStack backgroundColor="$background" minHeight="100vh">
        <Header />
        <YStack paddingTop={160} alignItems="center" gap="$4">
          <Text fontSize={24} fontWeight="600" color="$color12">
            Use case not found
          </Text>
          <Text
            fontSize={16}
            color="#ededed"
            cursor="pointer"
            onPress={() => navigate('/use-cases')}
          >
            View all use cases
          </Text>
        </YStack>
        <Footer />
      </YStack>
    );
  }

  const otherUseCases = USE_CASES.filter((uc) => uc.slug !== useCase.slug);

  return (
    <YStack backgroundColor="$background" minHeight="100vh">
      <Header />

      {/* Hero */}
      <YStack
        paddingTop={140}
        paddingBottom={80}
        paddingHorizontal="$5"
        maxWidth={900}
        alignSelf="center"
        width="100%"
        gap="$5"
        alignItems="center"
      >
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <XStack alignItems="center" gap="$2">
            <Text
              fontSize={13}
              color="$color8"
              cursor="pointer"
              hoverStyle={{ color: '$color11' }}
              onPress={() => navigate('/use-cases')}
              style={{ transition: 'color 0.15s ease' } as any}
            >
              Use Cases
            </Text>
            <CaretRight size={12} color="var(--color7)" />
            <Text fontSize={13} color="$color10">
              {useCase.title}
            </Text>
          </XStack>
        </motion.div>

        {/* Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <XStack
            width={64}
            height={64}
            borderRadius="$2"
            alignItems="center"
            justifyContent="center"
            style={{
              background: `linear-gradient(135deg, ${useCase.iconColor}22 0%, ${useCase.iconColor}11 100%)`,
              border: `1px solid ${useCase.iconColor}33`,
            }}
          >
            <useCase.icon size={32} color={useCase.iconColor} />
          </XStack>
        </motion.div>

        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={13}
            fontWeight="600"
            color={useCase.iconColor}
            letterSpacing={2}
            textTransform="uppercase"
          >
            {useCase.title}
          </Text>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={isMobile ? 32 : 48}
            fontWeight="700"
            textAlign="center"
            letterSpacing={-2}
            lineHeight={isMobile ? 38 : 54}
            color="$color12"
          >
            {useCase.headline}
          </Text>
        </motion.div>

        {/* Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={isMobile ? 16 : 18}
            textAlign="center"
            color="$color9"
            maxWidth={650}
            lineHeight={28}
          >
            {useCase.subtitle}
          </Text>
        </motion.div>
      </YStack>

      {/* Problem Section */}
      <YStack
        paddingVertical={80}
        paddingHorizontal="$5"
        maxWidth={900}
        alignSelf="center"
        width="100%"
        gap="$6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <YStack
            padding={isMobile ? '$5' : '$7'}
            borderRadius="$2"
            borderWidth={1}
            borderColor="$color4"
            backgroundColor="$color2"
            gap="$5"
          >
            <Text
              fontSize={13}
              fontWeight="600"
              color="#ededed"
              letterSpacing={2}
              textTransform="uppercase"
            >
              {useCase.problemLabel}
            </Text>
            <Text
              fontSize={isMobile ? 24 : 32}
              fontWeight="700"
              color="$color12"
              letterSpacing={-1}
              lineHeight={isMobile ? 30 : 40}
            >
              {useCase.problemHeadline}
            </Text>
            <Text
              fontSize={isMobile ? 15 : 16}
              color="$color9"
              lineHeight={26}
            >
              {useCase.problemDescription}
            </Text>
            <YStack gap="$3" marginTop="$2">
              {useCase.problemPoints.map((point, i) => (
                <XStack key={i} gap="$3" alignItems="flex-start">
                  <YStack
                    width={6}
                    height={6}
                    borderRadius="$2"
                    backgroundColor="$color7"
                    marginTop={8}
                    flexShrink={0}
                  />
                  <Text fontSize={15} color="$color10" lineHeight={24}>
                    {point}
                  </Text>
                </XStack>
              ))}
            </YStack>
          </YStack>
        </motion.div>
      </YStack>

      {/* Solution Section */}
      <YStack
        paddingVertical={80}
        paddingHorizontal="$5"
        maxWidth={900}
        alignSelf="center"
        width="100%"
        gap="$6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={13}
            fontWeight="600"
            color="#ededed"
            letterSpacing={2}
            textTransform="uppercase"
          >
            The Solution
          </Text>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={isMobile ? 28 : 38}
            fontWeight="700"
            textAlign="center"
            letterSpacing={-1.5}
            lineHeight={isMobile ? 34 : 46}
            color="$color12"
          >
            {useCase.solutionHeadline}
          </Text>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={isMobile ? 15 : 17}
            textAlign="center"
            color="$color9"
            maxWidth={650}
            lineHeight={28}
            alignSelf="center"
          >
            {useCase.solutionDescription}
          </Text>
        </motion.div>

        {/* Benefits */}
        <YStack gap="$5" marginTop="$4">
          {useCase.benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 + index * 0.08 }}
            >
              <XStack
                gap="$4"
                padding={isMobile ? '$4' : '$5'}
                borderRadius="$2"
                borderWidth={1}
                borderColor="$color4"
                backgroundColor="$color2"
                alignItems="flex-start"
                hoverStyle={{
                  borderColor: '$color6',
                  backgroundColor: '$color3',
                }}
                style={{ transition: 'all 0.2s ease' } as any}
              >
                <XStack
                  width={44}
                  height={44}
                  borderRadius="$2"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  <benefit.icon size={20} color="#ededed" />
                </XStack>
                <YStack gap="$1" flex={1}>
                  <Text
                    fontSize={isMobile ? 16 : 17}
                    fontWeight="600"
                    color="$color12"
                  >
                    {benefit.title}
                  </Text>
                  <Text
                    fontSize={14}
                    color="$color9"
                    lineHeight={22}
                  >
                    {benefit.description}
                  </Text>
                </YStack>
              </XStack>
            </motion.div>
          ))}
        </YStack>
      </YStack>

      {/* CTA Section */}
      <YStack
        paddingVertical={100}
        paddingHorizontal="$5"
        alignItems="center"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)',
        }}
      >
        <YStack alignItems="center" maxWidth={600} gap="$5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center' }}
          >
            <Text
              fontSize={isMobile ? 30 : 40}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1.5}
              lineHeight={isMobile ? 36 : 48}
              color="$color12"
            >
              {useCase.ctaHeadline}
            </Text>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ textAlign: 'center' }}
          >
            <Text
              fontSize={isMobile ? 16 : 18}
              textAlign="center"
              color="$color9"
              lineHeight={28}
            >
              {useCase.ctaSubtext}
            </Text>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <YStack marginTop="$4" width="100%" alignItems="center">
              <SubscribeForm variant="compact" />
            </YStack>
          </motion.div>
        </YStack>
      </YStack>

      {/* Other Use Cases */}
      <YStack
        paddingVertical={80}
        paddingHorizontal="$5"
        maxWidth={1100}
        alignSelf="center"
        width="100%"
        gap="$6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontSize={13}
            fontWeight="600"
            color="#ededed"
            letterSpacing={2}
            textTransform="uppercase"
          >
            More Use Cases
          </Text>
        </motion.div>

        <XStack
          flexWrap="wrap"
          gap="$4"
          justifyContent="center"
        >
          {otherUseCases.map((uc, index) => (
            <motion.div
              key={uc.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              style={{
                width: isMobile ? '100%' : 'calc(33.333% - 12px)',
                minWidth: isMobile ? undefined : 300,
                maxWidth: 360,
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/use-cases/${uc.slug}`)}
            >
              <YStack
                padding="$5"
                borderRadius="$2"
                borderWidth={1}
                borderColor="$color4"
                backgroundColor="$color2"
                gap="$3"
                hoverStyle={{
                  borderColor: '#ededed',
                  backgroundColor: '$color3',
                }}
                style={{ transition: 'all 0.2s ease' } as any}
                height="100%"
              >
                <XStack
                  width={36}
                  height={36}
                  borderRadius="$2"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    background: `linear-gradient(135deg, ${uc.iconColor}22 0%, ${uc.iconColor}11 100%)`,
                    border: `1px solid ${uc.iconColor}33`,
                  }}
                >
                  <uc.icon size={18} color={uc.iconColor} />
                </XStack>
                <Text fontSize={16} fontWeight="600" color="$color12">
                  {uc.title}
                </Text>
                <Text fontSize={14} color="$color9" lineHeight={22}>
                  {uc.headline}
                </Text>
                <XStack alignItems="center" gap="$1" marginTop="$1">
                  <Text fontSize={13} fontWeight="600" color="#ededed">
                    Learn more
                  </Text>
                  <ArrowRight size={12} color="#ededed" />
                </XStack>
              </YStack>
            </motion.div>
          ))}
        </XStack>
      </YStack>

      <Footer />
    </YStack>
  );
}
